import { Injectable, inject } from '@angular/core';
import { CatalogService } from '@services/data/catalog.service';
import { Request } from './requests.service';

/**
 * Motor BPM: lee procesos / statusFlow / roles / tipos de documento del catálogo.
 * No inventa reglas por slug de proceso ni por roles fijos cuando el flujo está configurado.
 */
@Injectable({ providedIn: 'root' })
export class BpmService {
  private catalogService = inject(CatalogService);

  /** Roles con privilegio total (no hay flag en catálogo de roles aún). */
  /** Solo ADM salta requiredRoles del BPM (auditoría clara). SUPPORT no. */
  private static readonly SUPERADMIN_ROLES = new Set(['ADM']);

  private normalizeRole(role: string | null | undefined): string {
    return (role ?? 'VISUALIZADOR').trim().toUpperCase();
  }

  private isSuperAdmin(userRole: string): boolean {
    return BpmService.SUPERADMIN_ROLES.has(this.normalizeRole(userRole));
  }

  /** Proceso del catálogo por slug. */
  processOf(slug: string | null | undefined): any | null {
    if (!slug) return null;
    return this.catalogService.processes().find((p: any) => p.slug === slug) ?? null;
  }

  /** Categoría del proceso (CLAIM, BENEFITS, MEMBERSHIP, …). */
  processCategory(slug: string | null | undefined): string | null {
    const cat = this.processOf(slug)?.category;
    return cat ? String(cat).trim().toUpperCase() : null;
  }

  isClaimProcess(slug: string | null | undefined): boolean {
    return this.processCategory(slug) === 'CLAIM';
  }

  isBenefitProcess(slug: string | null | undefined): boolean {
    return this.processCategory(slug) === 'BENEFITS';
  }

    isTransferProcess(slug: string | null | undefined): boolean {
    return this.processCategory(slug) === 'TRANSFER';
  }

  isUnsubscribeProcess(slug: string | null | undefined): boolean {
    const cat = this.processCategory(slug);
    if (cat === 'UNSUBSCRIBE' || cat === 'UNSUBSCRIBE_PARTY' || cat === 'UNSUBSCRIBE_ADM') return true;
    const s = String(slug || '').toUpperCase();
    return s === 'UNSUBSCRIBE' || s.startsWith('UNSUBSCRIBE_');
  }

  /** Socio/USER solo ve bajas de categoría PARTY. */
  canSeeUnsubscribeAdm(userRole?: string | null): boolean {
    const role = String(userRole || '').trim().toUpperCase();
    return role !== 'USER' && role !== 'USUARIO';
  }

  unsubscribeCategoriesForRole(userRole?: string | null): string[] {
    return this.canSeeUnsubscribeAdm(userRole)
      ? ['UNSUBSCRIBE_PARTY', 'UNSUBSCRIBE_ADM', 'UNSUBSCRIBE']
      : ['UNSUBSCRIBE_PARTY', 'UNSUBSCRIBE'];
  }

  isVoluntaryUnsubscribe(slug: string | null | undefined): boolean {
    const s = String(slug || '').toUpperCase();
    return s === 'UNSUBSCRIBE_VOLUNTARY' || s === 'UNSUBSCRIBE';
  }

  isVehicleUpdateProcess(slug: string | null | undefined): boolean {
    const s = String(slug || '').toUpperCase();
    return s === 'VEHICLE_QUICK_UPDATE' || s === 'VEHICLE_DATA_UPDATE'
      || this.processCategory(slug) === 'VEHICLE_UPDATE';
  }

  private statusSlugOf(flow: any): string | null {
    return flow?.status?.slug || flow?.statusSlug || null;
  }

  /** Normaliza pasos del snapshot JSON a la forma del catálogo. */
  private normalizeSnapshotSteps(steps: any[] | null | undefined): any[] {
    return [...(steps || [])]
      .filter((s: any) => s?.isActive !== false)
      .sort((a: any, b: any) => (a.stepSequence || 0) - (b.stepSequence || 0))
      .map((s: any) => ({
        ...s,
        status: s.status ?? { slug: s.statusSlug, name: s.statusName },
        documentType: s.documentType ?? (s.documentTypeSlug
          ? { slug: s.documentTypeSlug, name: s.documentTypeName }
          : null)
      }));
  }

  private extractSnapshotSteps(request: any): any[] | null {
    const raw = request?.flowSnapshot ?? request?.flow_snapshot;
    if (!raw) return null;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw)?.steps ?? null; } catch { return null; }
    }
    return raw?.steps ?? null;
  }

  /** Flujo de una solicitud: prioriza flowSnapshot; si no hay, catálogo. */
  sortedFlowFor(request: Request | null | undefined): any[] {
    const steps = this.extractSnapshotSteps(request);
    if (Array.isArray(steps) && steps.length) {
      return this.normalizeSnapshotSteps(steps);
    }
    return this.sortedFlow(request?.processSlug);
  }

  /** Flujo ordenado del proceso (status-flow del catálogo). */
  sortedFlow(processSlug: string | null | undefined): any[] {
    const flow = this.processOf(processSlug)?.statusFlow || [];
    return [...flow]
      .filter((step: any) => step?.isActive !== false)
      .sort((a: any, b: any) => a.stepSequence - b.stepSequence);
  }

  /** Paso cumplido si hay historial CONFIRMED saliendo de ese status (no a OBSERVED). */
  isStepCompletedInHistory(
    statusSlug: string | null | undefined,
    flowHistory: any[] | null | undefined
  ): boolean {
    if (!statusSlug) return false;
    const slug = String(statusSlug).toUpperCase();
    return (flowHistory || []).some((h) => {
      if (String(h?.status || '').toUpperCase() !== 'CONFIRMED') return false;
      if (String(h?.fromStatusSlug || '').toUpperCase() !== slug) return false;
      const to = String(h?.toStatusSlug || '').toUpperCase();
      if (to === 'OBSERVED') return false;
      return true;
    });
  }

  private firstUncompletedStep(flow: any[], history: any[]): any | null {
    if (!history?.length) return null;
    return flow.find((s: any) => !this.isStepCompletedInHistory(this.statusSlugOf(s), history)) ?? null;
  }

  private resolveGapStep(flow: any[], byStatus: any, pending: any): any | null {
    if (!byStatus || !pending) return null;
    const pIdx = flow.findIndex((f: any) => this.statusSlugOf(f) === this.statusSlugOf(pending));
    const sIdx = flow.findIndex((f: any) => this.statusSlugOf(f) === this.statusSlugOf(byStatus));
    return (pIdx >= 0 && sIdx >= 0 && pIdx < sIdx) ? pending : null;
  }

  /** Paso BPM activo: coincide con statusSlug salvo hueco atrás en el snapshot. */
  actingFlowStep(request: Request | null): any | null {
    if (!request) return null;
    const flow = this.sortedFlowFor(request);
    if (!flow.length) return null;
    const statusSlug = String(request.statusSlug ?? '').toUpperCase();
    const byStatus = flow.find((f: any) => this.statusSlugOf(f) === statusSlug) ?? null;
    const history = (request as any).flowHistory ?? [];
    const pending = this.firstUncompletedStep(flow, history);
    const gap = this.resolveGapStep(flow, byStatus, pending);
    return gap ?? byStatus ?? pending ?? flow[flow.length - 1] ?? null;
  }

  /**
   * True si el status de la solicitud está adelantado respecto al primer
   * paso pendiente del snapshot (p. ej. se insertó Borrador y quedó en Ministro Fe).
   */
  hasBehindGap(request: Request | null): boolean {
    if (!request?.statusSlug) return false;
    const acting = this.actingFlowStep(request);
    const actingSlug = this.statusSlugOf(acting);
    if (!actingSlug || actingSlug === request.statusSlug) return false;
    const flow = this.sortedFlowFor(request);
    const gapIdx = flow.findIndex((f: any) => this.statusSlugOf(f) === actingSlug);
    const curIdx = flow.findIndex((f: any) => this.statusSlugOf(f) === request.statusSlug);
    return gapIdx >= 0 && curIdx >= 0 && gapIdx < curIdx;
  }

  /** Slug del gap pendiente detrás del status actual, o null. */
  pendingGapStatusSlug(request: Request | null): string | null {
    if (!this.hasBehindGap(request)) return null;
    return this.statusSlugOf(this.actingFlowStep(request));
  }

  /** Etiqueta del paso gap (nombre de estado). */
  pendingGapLabel(request: Request | null): string | null {
    if (!this.hasBehindGap(request)) return null;
    const step = this.actingFlowStep(request);
    return step?.status?.name || step?.statusName || this.statusSlugOf(step);
  }

  /** Último paso de un flujo de 2+ etapas: no hay nada que aprobar. */
  isTerminalStatus(request: Request | null): boolean {
    if (!request) return false;
    const flow = this.sortedFlowFor(request);
    if (flow.length < 2) return false;
    const acting = this.actingFlowStep(request);
    return this.statusSlugOf(flow[flow.length - 1]) === this.statusSlugOf(acting);
  }

  currentFlowIndex(request: Request | null): number {
    if (!request) return -1;
    const flow = this.sortedFlowFor(request);
    const acting = this.actingFlowStep(request);
    const actingSlug = this.statusSlugOf(acting);
    if (actingSlug) {
      const byActing = flow.findIndex((f: any) => this.statusSlugOf(f) === actingSlug);
      if (byActing >= 0) return byActing;
    }
    return flow.findIndex((f: any) => this.statusSlugOf(f) === request.statusSlug);
  }

  /**
   * Paso inicial = primer paso del snapshot o del catálogo.
   */
  isInitialStep(request: any | null): boolean {
    if (!request) return false;
    const flow = this.sortedFlowFor(request);
    if (!flow.length) return false;
    const acting = this.actingFlowStep(request);
    return this.statusSlugOf(flow[0]) === this.statusSlugOf(acting);
  }

  /** Primer paso del flujo BPM del proceso (catálogo). */
  initialFlowStep(processSlug: string | null | undefined): any | null {
    const flow = this.sortedFlow(processSlug);
    return flow[0] ?? null;
  }

  /** Slug del estado inicial según BPM (null si el proceso no tiene flujo). */
  initialStatusSlug(processSlug: string | null | undefined): string | null {
    return this.statusSlugOf(this.initialFlowStep(processSlug));
  }

  /** El paso inicial del proceso exige documento al crear/enviar (status-flow). */
  initialStepRequiresDocument(processSlug: string | null | undefined): boolean {
    const step = this.initialFlowStep(processSlug);
    if (!step) return false;
    const flag = step.requiresDocument ?? step.requires_document;
    return flag === true || flag === 1 || flag === 'true' || flag === '1';
  }

  /** Tipo de documento del paso inicial (catálogo), o null. */
  initialStepDocumentType(processSlug: string | null | undefined): any | null {
    const step = this.initialFlowStep(processSlug);
    if (!step?.requiresDocument) return null;
    return step.documentType
      ?? this.catalogService.getDocumentTypeBySlug(step.documentTypeSlug)
      ?? null;
  }

  /** Nombre del documento exigido al iniciar el proceso. */
  initialStepDocumentTypeName(
    processSlug: string | null | undefined,
    fallback = 'Documento'
  ): string {
    const docType = this.initialStepDocumentType(processSlug);
    return docType?.name ?? docType?.slug ?? fallback;
  }

  /** Siguiente paso configurado en el flujo BPM. */
  nextFlowStep(request: any | null): any | null {
    if (!request) return null;
    const flow = this.sortedFlowFor(request);
    const idx = this.currentFlowIndex(request);
    if (idx < 0 || idx >= flow.length - 1) return null;
    return flow[idx + 1] ?? null;
  }

  isAwaitingInvoiceStep(request: Request | null): boolean {
    return String(request?.statusSlug || '').toUpperCase() === 'AWAITING_INVOICE';
  }

  isCommissionApprovalStep(request: Request | null): boolean {
    const isCommission = String(request?.statusSlug || '').toUpperCase() === 'MINISTRY_APPROVED';
    return isCommission && this.isClaimProcess(request?.processSlug);
  }

  stepRequiresDocument(request: Request | null): boolean {
    return !!this.actingFlowStep(request)?.requiresDocument;
  }

  stepDocumentType(request: Request | null): any | null {
    const step = this.actingFlowStep(request);
    if (!step?.requiresDocument) return null;
    return step.documentType
      ?? this.catalogService.getDocumentTypeBySlug(step.documentTypeSlug)
      ?? null;
  }

  stepDocumentTypeSlug(request: Request | null): string | null {
    const step = this.actingFlowStep(request);
    return step?.requiresDocument ? (step.documentTypeSlug ?? null) : null;
  }

  stepDocumentTypeName(request: Request | null, fallback = 'Documento'): string {
    const docType = this.stepDocumentType(request);
    return docType?.name ?? docType?.slug ?? fallback;
  }

  /**
   * Estados de liquidación real en la solicitud (legacy).
   * Con BPM actual el cierre es APPROVED; PAID/REIMBURSED viven en reimbursement.
   */
  private isSettledStatusSlug(slug: string | null | undefined): boolean {
    if (!slug) return false;
    const key = String(slug).toUpperCase();
    return key === 'PAID' || key === 'REIMBURSED';
  }

  private isPaymentProcessSlug(slug: string | null | undefined): boolean {
    if (!slug) return false;
    const key = String(slug).toUpperCase();
    return key === 'PENDING_PAYMENT' || key === 'PENDING_REIMBURSEMENT';
  }

  /** Tipo de documento del catálogo asociado a comprobante de pago/reembolso. */
  private isPaymentDocumentSlug(slug: string | null | undefined): boolean {
    if (!slug) return false;
    const key = String(slug).toUpperCase();
    if (/VOTO|ACTA|COMISION|MINISTER/.test(key)) return false;
    if (/PAGO|PAYMENT|RECEIPT|COMPROBANTE|REEMBOLSO/.test(key)) return true;
    const doc = this.catalogService.getDocumentTypeBySlug(String(slug));
    const name = String(doc?.name || '').toUpperCase();
    if (/VOTO|ACTA|COMISI/.test(name)) return false;
    return /PAGO|COMPROBANTE|REEMBOLSO|RECEIPT/.test(name);
  }

  /**
   * Paso de pago/reembolso: estado actual PENDING_* , siguiente legacy PAID/REIMBURSED,
   * o documento del paso es comprobante (no voto).
   */
  isPaymentStep(request: Request | null): boolean {
    if (!request || this.isSettledStatusSlug(request.statusSlug)) return false;
    if (this.isPaymentProcessSlug(request.statusSlug)) return true;
    if (this.isSettledStatusSlug(this.nextStatusSlug(request))) return true;
    return this.isPaymentDocumentSlug(this.stepDocumentTypeSlug(request));
  }

  private requiredRolesOf(flow: any): string[] {
    const raw = flow?.requiredRoles;
    if (Array.isArray(raw) && raw.length) {
      return raw.map((r) => String(r).trim().toUpperCase()).filter(Boolean);
    }
    const single = String(flow?.requiredRole || '').trim().toUpperCase();
    return single && single !== '*' ? [single] : [];
  }

  private flowRoleNames(flow: any): string[] {
    const names = [...this.requiredRolesOf(flow)];
    const extra = this.extraordinaryRoleOf(flow);
    if (extra && !names.includes(extra)) names.push(extra);
    return names;
  }

  private originFlowStep(request: Request | null): any {
    if (!request) return null;
    const steps = this.sortedFlowFor(request);
    return steps[0] ?? null;
  }

  private hasOriginAction(request: Request | null): boolean {
    if (!request) return false;
    const status = String(request.statusSlug || '').toUpperCase();
    if (status === 'OBSERVED' || status === 'DRAFT') return true;
    if (request.needsOriginInbox) return true;
    return this.hasObservedClaimDocuments(request) || this.hasPendingCorrectionDocuments(request);
  }

  /** Origen: roles autorizados en el paso inicial del BPM. */
  matchesOriginInboxAccess(request: Request | null, userRole: string, _userMail?: string | null): boolean {
    if (!request || !this.hasOriginAction(request)) return false;
    const role = this.normalizeRole(userRole);
    const initial = this.originFlowStep(request);
    return !initial || this.matchesFlowRoles(initial, role, false);
  }

  extraordinaryRoleOf(flow: any): string | null {
    const role = String(flow?.alternateRole || '').trim().toUpperCase();
    if (!role || role === '*') return null;
    const until = flow?.alternateRoleUntil;
    if (!until) return null;
    const end = new Date(`${String(until).slice(0, 10)}T23:59:59.999`);
    if (!Number.isFinite(end.getTime()) || Date.now() > end.getTime()) return null;
    return role;
  }

  private matchesRequiredRole(
    requiredRole: string | null | undefined,
    role: string,
    isSuperAdmin: boolean
  ): boolean {
    if (isSuperAdmin) return true;
    const required = String(requiredRole || '').trim().toUpperCase();
    if (!required || required === '*') return true;
    return required === role;
  }

  private matchesFlowRoles(flow: any, role: string, isSuperAdmin: boolean): boolean {
    if (isSuperAdmin) return true;
    const allowed = this.flowRoleNames(flow);
    if (!allowed.length) return true; // requiredRole vacío / * → cualquier rol autorizado
    return allowed.includes(role);
  }

  /** Si el rol puede iniciar o gestionar el paso inicial del proceso. */
  canInitiateProcess(processSlug: string, userRole: string): boolean {
    if (!processSlug || !userRole) return false;
    if (this.isSuperAdmin(userRole)) return true;
    const initialStep = this.sortedFlow(processSlug)[0];
    if (!initialStep) return false;
    return this.matchesFlowRoles(initialStep, this.normalizeRole(userRole), false);
  }

  /** Cerrado si es APPROVED, terminal, o último paso del flujo propio. */
  isClosed(request: Request | null): boolean {
    if (!request?.processSlug || !request.statusSlug) return false;
    const slug = String(request.statusSlug).toUpperCase();
    if (slug === 'APPROVED' || slug === 'REJECTED' || slug === 'EXPIRED' || slug === 'DISCARDED') return true;
    if (slug === 'OBSERVED') return false;
    const flow = this.sortedFlowFor(request);
    if (!flow.length) return false;
    const idx = flow.findIndex((step: any) => this.statusSlugOf(step) === slug);
    return idx === -1 ? slug !== 'DRAFT' : idx >= flow.length - 1;
  }

  /** Borrador desechado (recuperable desde historial). */
  isDiscarded(request: Request | null | undefined): boolean {
    return String(request?.statusSlug || '').toUpperCase() === 'DISCARDED';
  }

  /** Glosa/voto del historial BPM (commissionDecision o flowHistory). */
  documentGloss(request: Request | null | undefined): string {
    if (!request) return '';
    const fromDecision = String(request.commissionDecision?.gloss ?? '').trim();
    if (fromDecision) return fromDecision;
    const fromHistory = (request.flowHistory || [])
      .map((h) => String(h?.gloss ?? '').trim())
      .find((g) => !!g);
    return fromHistory || '';
  }

  isFlowOpen(request: Request | null): boolean {
    return !this.isClosed(request);
  }

  /**
   * Quién puede avanzar el paso actual: solo `requiredRole` (+ alternate) del status-flow.
   * Sin flujo configurado o sin paso para el estado → no aprueba.
   */
  canApprove(request: Request | null, userRole: string): boolean {
    if (!request || this.isClosed(request)) return false;
    if (this.isDraft(request)) return false;
    if (this.isObserved(request)) return false;

    const role = this.normalizeRole(userRole);
    const acting = this.actingFlowStep(request);
    if (!acting) return false;

    return this.matchesFlowRoles(acting, role, this.isSuperAdmin(role));
  }

  /** Solicitud aún en borrador (no entró al flujo BPM). */
  isDraft(request: Request | null | undefined): boolean {
    return String(request?.statusSlug || '').toUpperCase() === 'DRAFT';
  }

  // Destino según BPM: paso posterior a Borrador o siguiente en el flujo
  submitTargetStatusSlug(request: Request | null): string {
    if (!request) return '';
    if (this.isDraft(request)) {
      const flow = this.sortedFlowFor(request);
      const draftIdx = flow.findIndex((f: any) => this.statusSlugOf(f) === 'DRAFT');
      if (draftIdx >= 0 && draftIdx < flow.length - 1) {
        return this.statusSlugOf(flow[draftIdx + 1]) ?? '';
      }
      return this.initialStatusSlug(request.processSlug) ?? '';
    }
    return this.nextStatusSlug(request);
  }

  /**
   * Enviar borrador desde wizard (add-party / add-claim).
   * No usa requiredRole del paso inicial: quien arma el draft puede enviarlo.
   */
  canSubmitDraft(request: Request | null): boolean {
    return this.isDraft(request) && !!this.submitTargetStatusSlug(request);
  }

  /** Rol revisor del paso (`contentReviewRole`), o null. */
  contentReviewRole(request: Request | null): string | null {
    const reviewer = this.actingFlowStep(request)?.contentReviewRole;
    if (reviewer && String(reviewer).trim() && String(reviewer).trim() !== '*') {
      return String(reviewer).trim().toUpperCase();
    }
    return null;
  }

  /**
   * @deprecated Usar `stepRequiresDocument`. El adjunto lo define `requiresDocument`
   * en status-flow; `contentReviewRole` es solo quién revisa contenido.
   */
  stepRequiresCommissionVoto(request: Request | null): boolean {
    return this.stepRequiresDocument(request);
  }

  /** Quién puede revisar daños/evidencias (`contentReviewRole` del paso). */
  canReviewContent(request: Request | null, userRole: string): boolean {
    if (!request) return false;
    const role = this.normalizeRole(userRole);
    const reviewer = this.contentReviewRole(request);
    if (!reviewer) return false;
    return this.matchesRequiredRole(reviewer, role, this.isSuperAdmin(role));
  }

  /**
   * ¿El paso actual es el último del flujo con `contentReviewRole`?
   * Intermedios = solo Observar; el último puede aprobar (docs) y Observar.
   */
  isLastContentReviewStep(request: Request | null): boolean {
    if (!request) return false;
    const acting = this.actingFlowStep(request);
    if (!String(acting?.contentReviewRole || '').trim()) return false;
    const steps = this.sortedFlowFor(request);
    const withReviewer = steps.filter((s: any) => !!String(s?.contentReviewRole || '').trim());
    if (!withReviewer.length) return false;
    const last = withReviewer[withReviewer.length - 1];
    return String(this.statusSlugOf(acting) || '').toUpperCase()
      === String(this.statusSlugOf(last) || '').toUpperCase();
  }

  /** Revisor final: último paso con rol revisor, único que aprueba documentos. */
  canFinalContentReview(request: Request | null, userRole: string): boolean {
    if (!request) return false;
    return this.isLastContentReviewStep(request) && this.canReviewContent(request, userRole);
  }

  isObserved(request: Request | null | undefined): boolean {
    return String(request?.statusSlug || '').toUpperCase() === 'OBSERVED';
  }

  /** Paso del catálogo vivo con el mismo statusSlug (para flags nuevos no congelados). */
  private liveStepFor(request: Request | null, statusSlug: string | null): any | null {
    if (!request?.processSlug || !statusSlug) return null;
    const target = String(statusSlug).toUpperCase();
    return this.sortedFlow(request.processSlug).find(
      (f: any) => String(this.statusSlugOf(f) || '').toUpperCase() === target
    ) ?? null;
  }

  /**
   * Config de observar: hay rol revisor en el paso (snapshot o catálogo vivo).
   * No hay switch aparte: revisor ⇒ puede Observar.
   */
  private observeConfig(request: Request | null): {
    allowsObserve: boolean;
    contentReviewRole: string | null;
    observeToStatusSlug: string;
    resubmitToStatusSlug: string | null;
    acting: any | null;
  } {
    const acting = this.actingFlowStep(request);
    const live = this.liveStepFor(request, this.statusSlugOf(acting));
    const contentReviewRole = String(
      acting?.contentReviewRole || live?.contentReviewRole || ''
    ).trim() || null;
    const allowsObserve = !!contentReviewRole;
    return {
      allowsObserve,
      contentReviewRole,
      observeToStatusSlug: String(
        acting?.observeToStatusSlug || live?.observeToStatusSlug || 'OBSERVED'
      ).toUpperCase(),
      resubmitToStatusSlug: (acting?.resubmitToStatusSlug || live?.resubmitToStatusSlug || null)
        ? String(acting?.resubmitToStatusSlug || live?.resubmitToStatusSlug).toUpperCase()
        : null,
      acting
    };
  }

  /** Paso con rol revisor (quien define destino al subsanar). */
  observeCapableStep(request: Request | null): any | null {
    const hasReviewer = (s: any) => !!String(s?.contentReviewRole || '').trim();
    const fromSnap = this.sortedFlowFor(request).find(hasReviewer);
    if (fromSnap) return fromSnap;
    return this.sortedFlow(request?.processSlug).find(hasReviewer) ?? null;
  }

  observeTargetSlug(request: Request | null): string {
    return this.observeConfig(request).observeToStatusSlug || 'OBSERVED';
  }

  resubmitTargetSlug(request: Request | null): string {
    const cfg = this.observeConfig(request);
    if (cfg.resubmitToStatusSlug) return cfg.resubmitToStatusSlug;
    const step = this.observeCapableStep(request);
    const configured = step?.resubmitToStatusSlug;
    if (configured) return String(configured).toUpperCase();
    const hist = (request?.flowHistory || [])
      .slice()
      .reverse()
      .find((h: any) => String(h?.toStatusSlug || '').toUpperCase() === 'OBSERVED');
    if (hist?.fromStatusSlug) return String(hist.fromStatusSlug).toUpperCase();
    return step?.statusSlug ? String(step.statusSlug).toUpperCase() : '';
  }

  /** Motivo de la última observación (historial hacia OBSERVED). */
  observationReason(request: Request | null | undefined): string {
    if (!request) return '';
    const hist = (request.flowHistory || [])
      .slice()
      .reverse()
      .find((h: any) => String(h?.toStatusSlug || '').toUpperCase() === 'OBSERVED');
    return String(hist?.comments ?? hist?.gloss ?? hist?.rejectionReason ?? '').trim();
  }

  /**
   * Puede observar el contentReviewRole del paso actual.
   * Tener rol revisor implica la opción Observar (sin flag aparte).
   */
  canObserve(request: Request | null, userRole: string): boolean {
    if (!request || this.isClosed(request) || this.isObserved(request) || this.isDraft(request)) {
      return false;
    }
    const cfg = this.observeConfig(request);
    if (!cfg.contentReviewRole) return false;
    const role = this.normalizeRole(userRole);
    return this.matchesRequiredRole(cfg.contentReviewRole, role, this.isSuperAdmin(role));
  }

  /** En OBSERVED: permite reenviar a revisión a cualquier rol del paso inicial del BPM. */
  canResubmitObservation(request: Request | null, userRole: string, userMail?: string | null): boolean {
    if (!request || !this.isObserved(request)) return false;
    if (!this.resubmitTargetSlug(request)) return false;
    const role = this.normalizeRole(userRole);
    if (this.isSuperAdmin(role)) return true;
    return this.matchesOriginInboxAccess(request, userRole, userMail);
  }

  /** Siguiente estado según el flujo; si no hay siguiente, el actual. */
  nextStatusSlug(request: Request | null): string {
    if (!request) return '';
    if (this.isTerminalStatus(request)) return request.statusSlug;

    const next = this.nextFlowStep(request);
    return this.statusSlugOf(next) || request.statusSlug || '';
  }

  /** Rol requerido por el paso actual (`requiredRole`). */
  requiredRole(request: Request | null): string | null {
    const required = this.actingFlowStep(request)?.requiredRole;
    if (required && String(required).trim() && String(required).trim() !== '*') {
      return String(required).trim().toUpperCase();
    }
    return null;
  }

  /**
   * Tras aprobar, el mismo rol sigue actuando en el siguiente estado
   * (p. ej. aprueba con documento y luego registra el pago).
   */
  shouldStayOpenAfterAdvance(
    request: Request | null,
    nextStatusSlug: string,
    userRole: string
  ): boolean {
    if (!request || !nextStatusSlug) return false;
    if (nextStatusSlug === 'REJECTED' || this.isSettledStatusSlug(nextStatusSlug)) return false;
    const nextRequest = { ...request, statusSlug: nextStatusSlug };
    if (this.canApprove(nextRequest, userRole)) return true;
    return this.isPaymentStep(nextRequest) && this.canApprove(request, userRole);
  }

  /** Filtra bandeja: paso BPM actual, revisor u origen (paso 0 + correo). */
  filterByRole(requests: Request[], userRole: string, userMail?: string | null): Request[] {
    const role = this.normalizeRole(userRole);
    const isSuperAdmin = this.isSuperAdmin(role);

    return requests.filter((item) => {
      if (isSuperAdmin) return true;

      if (this.isObserved(item)) {
        if (this.canResubmitObservation(item, role, userMail)) return true;
        const acting = this.actingFlowStep(item);
        const reviewer = String(acting?.contentReviewRole || '').trim().toUpperCase();
        return (!!acting && this.matchesFlowRoles(acting, role, false)) || (!!reviewer && reviewer === role);
      }

      if (this.matchesOriginInboxAccess(item, role, userMail)) return true;

      const acting = this.actingFlowStep(item);
      if (acting) {
        if (this.matchesFlowRoles(acting, role, false)) return true;
        const reviewer = String(acting.contentReviewRole || '').trim().toUpperCase();
        if (reviewer && reviewer === role) return true;
        return false;
      }

      return false;
    });
  }

  /** Roles preferidos para actuar: paso actual (+ revisor) y origen si hay acción pendiente. */
  preferredRoleNames(request: Request | null): string[] {
    if (!request) return [];
    const names = new Set<string>();
    const acting = this.actingFlowStep(request);
    for (const n of this.flowRoleNames(acting)) {
      if (n) names.add(this.normalizeRole(n));
    }
    const reviewer = this.contentReviewRole(request);
    if (reviewer) names.add(reviewer);
    if (this.hasOriginAction(request)) {
      for (const n of this.flowRoleNames(this.originFlowStep(request))) {
        if (n) names.add(this.normalizeRole(n));
      }
    }
    return [...names];
  }

  /** Privilegio de paso: aprobar / revisar / observar (no solo ver bandeja origen). */
  hasStepPrivilege(request: Request | null, userRole: string): boolean {
    if (!request) return false;
    const role = this.normalizeRole(userRole);
    if (this.isSuperAdmin(role)) return true;
    return (
      this.canApprove(request, role) ||
      this.canReviewContent(request, role) ||
      this.canObserve(request, role)
    );
  }

  /** Puede actuar en la solicitud con el rol indicado (aprobar, revisar, origen, etc.). */
  canActOnRequest(request: Request | null, userRole: string, userMail?: string | null): boolean {
    if (!request) return false;
    const role = this.normalizeRole(userRole);
    if (this.isSuperAdmin(role)) return true;
    if (this.hasStepPrivilege(request, role)) return true;
    if (this.canResubmitObservation(request, role, userMail)) return true;
    if (this.matchesOriginInboxAccess(request, role, userMail)) return true;
    if (this.canReplaceObservedDocuments(request, role, userMail)) return true;
    return false;
  }

  /**
   * Rol asignado con el que conviene gestionar la solicitud (siempre el mejor).
   * Prioriza privilegio de paso BPM (aprobar/revisar) sobre solo ver origen.
   */
  resolveInboxRole(
    request: Request | null,
    roles: Array<{ id?: number; name?: string; slug?: string; displayName?: string; description?: string } | null | undefined>,
    userMail?: string | null
  ): { id?: number; name?: string; slug?: string; displayName?: string; description?: string } | null {
    if (!request || !Array.isArray(roles) || !roles.length) return null;
    const assigned = roles.filter((r) => r?.id != null && (r?.slug ?? r?.name));

    const actingNames = new Set(
      this.flowRoleNames(this.actingFlowStep(request)).map((n) => this.normalizeRole(n))
    );
    const reviewer = this.contentReviewRole(request);
    if (reviewer) actingNames.add(reviewer);

    const stepActors = assigned.filter((r) => this.hasStepPrivilege(request, (r as any).slug ?? r!.name));
    const originActors = assigned.filter(
      (r) =>
        !stepActors.some((s) => Number(s!.id) === Number(r!.id)) &&
        this.canActOnRequest(request, (r as any).slug ?? r!.name, userMail)
    );

    const ranked: typeof assigned = [];
    const pushUnique = (r: (typeof assigned)[number]) => {
      if (!r?.id || ranked.some((x) => Number(x!.id) === Number(r.id))) return;
      ranked.push(r);
    };

    for (const r of stepActors) {
      if (actingNames.has(this.normalizeRole((r as any).slug ?? r!.name))) pushUnique(r);
    }
    for (const r of stepActors) pushUnique(r);
    for (const r of originActors) pushUnique(r);

    return ranked[0] || null;
  }

  /**
   * Rol al que hay que cambiar al abrir (null si el activo ya es el correcto).
   */
  pickRoleForRequest(
    request: Request | null,
    roles: Array<{ id?: number; name?: string; slug?: string } | null | undefined>,
    currentRoleName?: string | null,
    userMail?: string | null
  ): { id?: number; name?: string; slug?: string } | null {
    const best = this.resolveInboxRole(request, roles, userMail);
    if (!best) return null;
    const bestIdent = (best as any)?.slug ?? best.name;
    if (this.normalizeRole(bestIdent) === this.normalizeRole(currentRoleName)) return null;
    return best;
  }

  private claimDocumentsOf(request: Request | null): any[] {
    const claim = (request as any)?.claim;
    if (!claim) return [];
    const top = Array.isArray(claim.documents) ? claim.documents : [];
    const nested = (claim.details || []).flatMap((d: any) =>
      Array.isArray(d.documents) ? d.documents : []
    );
    return [...top, ...nested];
  }

  private docStatusSlug(doc: any): string {
    return String(doc?.statusSlug || doc?.status_slug || doc?.status?.slug || '').toUpperCase();
  }

  hasObservedClaimDocuments(request: Request | null): boolean {
    return this.claimDocumentsOf(request).some((d) => this.docStatusSlug(d) === 'OBSERVED');
  }

  private hasPendingCorrectionDocuments(request: Request | null): boolean {
    const fromClaim = this.claimDocumentsOf(request).some((d) => {
      if (this.docStatusSlug(d) !== 'PENDING') return false;
      return !!String(d?.reviewComment || d?.review_comment || '').trim();
    });
    if (fromClaim) return true;
    const slots = Array.isArray((request as any)?.requestDocuments)
      ? (request as any).requestDocuments
      : [];
    return slots.some((d: any) => {
      if (this.docStatusSlug(d) !== 'PENDING') return false;
      return !!String(d?.reviewComment || d?.review_comment || '').trim();
    });
  }

  /** @deprecated Use matchesOriginInboxAccess */
  isVisibleToOriginInbox(request: Request | null, userRole: string, userMail?: string | null): boolean {
    return this.matchesOriginInboxAccess(request, userRole, userMail);
  }

  /** Puede corregir evidencias/documentos observados: paso origen BPM + correo creador. */
  canReplaceObservedDocuments(request: Request | null, userRole: string, userMail?: string | null): boolean {
    if (!request) return false;
    const needsFix = this.isObserved(request)
      || this.hasObservedClaimDocuments(request)
      || this.hasPendingCorrectionDocuments(request);
    if (!needsFix) return false;
    const role = this.normalizeRole(userRole);
    if (this.isSuperAdmin(role)) return true;
    return this.matchesOriginInboxAccess(request, userRole, userMail);
  }
}
